import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "../components/Card";
import { CenteredSpinner } from "../components/CenteredSpinner";
import { ErrorBanner } from "../components/ErrorBanner";
import { PasswordForm } from "../components/PasswordForm";
import { supabase } from "../lib/supabase";
import { describeAuthError } from "../lib/auth-errors";
import { strings } from "../lib/strings";

type Stage = "verifying" | "form" | "saving" | "success" | "invalid";

export type RecoveryOtpType = "invite" | "recovery";

interface PasswordRecoveryPageProps {
  otpType: RecoveryOtpType;
  title: string;
  lead: string;
  successTitle: string;
  successBody: string;
  invalidLinkTitle: string;
  invalidLinkBody: string;
}

export function PasswordRecoveryPage({
  otpType,
  title,
  lead,
  successTitle,
  successBody,
  invalidLinkTitle,
  invalidLinkBody,
}: PasswordRecoveryPageProps): JSX.Element {
  const [params] = useSearchParams();
  const tokenHash: string | null = params.get("token_hash");

  const [stage, setStage] = useState<Stage>("verifying");
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    if (!tokenHash) {
      setStage("invalid");
      return;
    }
    let cancelled = false;
    const verify = async (): Promise<void> => {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHash,
        type: otpType,
      });
      if (cancelled) return;
      if (error) {
        setServerError(describeAuthError(error));
        setStage("invalid");
        return;
      }
      setStage("form");
    };
    void verify();
    return () => {
      cancelled = true;
    };
  }, [tokenHash, otpType]);

  const submitPassword = async (password: string): Promise<void> => {
    setStage("saving");
    setServerError(null);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setServerError(describeAuthError(error));
      setStage("form");
      return;
    }
    await supabase.auth.signOut();
    setStage("success");
  };

  if (stage === "verifying") {
    return (
      <Card title={title} subtitle={strings.common.loading}>
        <CenteredSpinner />
      </Card>
    );
  }

  if (stage === "invalid") {
    return (
      <Card title={invalidLinkTitle} subtitle={invalidLinkBody}>
        <ErrorBanner message={serverError} />
      </Card>
    );
  }

  if (stage === "success") {
    return (
      <Card>
        <div className="success">
          <span className="success__icon" aria-hidden="true">
            ✓
          </span>
          <h1 className="success__title">{successTitle}</h1>
          <p className="success__body">{successBody}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title={title} subtitle={lead}>
      <PasswordForm
        isSubmitting={stage === "saving"}
        onSubmit={submitPassword}
        serverError={serverError}
      />
    </Card>
  );
}
